import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../shared';
import { Label } from './ui/label';
import { SearchableSelect } from './ui/searchable-select';
import { Switch } from './ui/switch';
import { Building2, BedDouble, Utensils, Calendar } from 'lucide-react';

/**
 * Cascading Service Selector
 * 4 levels: Service → Hotel → Room → Meals toggle
 * Automatically emits total_price = room.price + (has_meals ? room.meal_price : 0)
 *
 * props:
 *   value: { service_id, hotel_id, room_id, has_meals }
 *   onChange: (next) => void  // emits full value + computed total + label strings
 *   compact?: boolean          // smaller UI
 *   required?: boolean
 */
const CascadingServiceSelector = ({ value = {}, onChange, compact = false, required = false }) => {
  const [services, setServices] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // Load active services
  useEffect(() => {
    axios.get(`${API}/hierarchical-services?is_active=true`, { headers: getAuthHeaders() })
      .then(r => setServices(r.data || []))
      .catch(e => console.error('Load services', e));
  }, []);

  // When service_id changes, load hotels
  useEffect(() => {
    if (!value.service_id) { setHotels([]); setSelectedService(null); return; }
    const svc = services.find(s => s.id === value.service_id);
    setSelectedService(svc);
    axios.get(`${API}/hierarchical-services/hotels/by-service/${value.service_id}`, { headers: getAuthHeaders() })
      .then(r => setHotels(r.data || []))
      .catch(e => console.error('Load hotels', e));
  }, [value.service_id, services]);

  // When hotel_id changes, load rooms
  useEffect(() => {
    if (!value.hotel_id) { setRooms([]); setSelectedHotel(null); return; }
    const h = hotels.find(x => x.id === value.hotel_id);
    setSelectedHotel(h);
    axios.get(`${API}/hierarchical-services/rooms/by-hotel/${value.hotel_id}`, { headers: getAuthHeaders() })
      .then(r => setRooms(r.data || []))
      .catch(e => console.error('Load rooms', e));
  }, [value.hotel_id, hotels]);

  // Track selected room
  useEffect(() => {
    const r = rooms.find(x => x.id === value.room_id);
    setSelectedRoom(r);
  }, [value.room_id, rooms]);

  const emit = (patch) => {
    const next = { ...value, ...patch };
    // compute total
    let room = selectedRoom;
    if (patch.room_id !== undefined) room = rooms.find(r => r.id === patch.room_id);
    const total = room ? (room.price + (next.has_meals ? (room.meal_price || 0) : 0)) : 0;

    // Build a human readable label
    const svcName = (patch.service_id !== undefined
      ? services.find(s => s.id === patch.service_id)?.name
      : selectedService?.name) || '';
    const hotelName = (patch.hotel_id !== undefined
      ? hotels.find(h => h.id === patch.hotel_id)?.name
      : selectedHotel?.name) || '';
    const roomName = room?.room_type || '';
    const mealsStr = next.has_meals ? ' (مع إعاشة)' : '';
    const service_label = [svcName, hotelName, roomName].filter(Boolean).join(' - ') + mealsStr;

    onChange({
      ...next,
      total_price: total,
      service_label,
      room_price: room?.price || 0,
      meal_price: (next.has_meals ? (room?.meal_price || 0) : 0)
    });
  };

  const setServiceId = (sid) => emit({ service_id: sid, hotel_id: null, room_id: null, has_meals: false });
  const setHotelId = (hid) => emit({ hotel_id: hid, room_id: null, has_meals: false });
  const setRoomId = (rid) => emit({ room_id: rid, has_meals: false });
  const setHasMeals = (v) => emit({ has_meals: v });

  const total = selectedRoom ? (selectedRoom.price + (value.has_meals ? (selectedRoom.meal_price || 0) : 0)) : 0;

  return (
    <div className={`space-y-3 ${compact ? 'text-sm' : ''}`}>
      {/* 1. Service */}
      <div>
        <Label className="flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5 text-brand-600" />
          الخدمة {required && <span className="text-red-500">*</span>}
        </Label>
        <SearchableSelect
          value={value.service_id || ''}
          onChange={setServiceId}
          options={services.map(s => ({
            value: s.id,
            label: s.name,
            hint: s.departure_date || '',
            search: `${s.name} ${s.departure_date || ''} ${s.description || ''}`,
          }))}
          placeholder={services.length === 0 ? 'لا توجد خدمات' : 'اختر الخدمة أو ابحث...'}
          searchPlaceholder="🔎 ابحث باسم الخدمة..."
          emptyText="لا توجد خدمة مطابقة"
          testId="css-service-trigger"
        />
      </div>

      {/* 2. Hotel */}
      {value.service_id && (
        <div>
          <Label className="flex items-center gap-1">
            <Building2 className="h-3.5 w-3.5 text-green-600" />
            الفندق {required && <span className="text-red-500">*</span>}
          </Label>
          <SearchableSelect
            value={value.hotel_id || ''}
            onChange={setHotelId}
            options={hotels.map(h => ({
              value: h.id,
              label: h.name,
              hint: h.city || '',
              search: `${h.name} ${h.city || ''}`,
            }))}
            placeholder={hotels.length === 0 ? 'لا توجد فنادق مرتبطة بهذه الخدمة' : 'اختر الفندق...'}
            searchPlaceholder="🔎 ابحث باسم الفندق..."
            emptyText="لا يوجد فندق مطابق"
            testId="css-hotel-trigger"
          />
        </div>
      )}

      {/* 3. Room */}
      {value.hotel_id && (
        <div>
          <Label className="flex items-center gap-1">
            <BedDouble className="h-3.5 w-3.5 text-purple-600" />
            نوع الغرفة {required && <span className="text-red-500">*</span>}
          </Label>
          <SearchableSelect
            value={value.room_id || ''}
            onChange={setRoomId}
            options={rooms.map(r => ({
              value: r.id,
              label: r.room_type,
              hint: `${(r.price || 0).toLocaleString()} دج${r.meal_price ? ` (+${r.meal_price.toLocaleString()} إعاشة)` : ''}`,
              search: `${r.room_type}`,
            }))}
            placeholder={rooms.length === 0 ? 'لا توجد غرف لهذا الفندق' : 'اختر نوع الغرفة...'}
            searchPlaceholder="🔎 ابحث بنوع الغرفة..."
            emptyText="لا توجد غرفة مطابقة"
            testId="css-room-trigger"
          />
        </div>
      )}

      {/* 4. Meals Toggle */}
      {value.room_id && selectedRoom && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded p-3">
          <div className="flex items-center gap-2">
            <Utensils className="h-4 w-4 text-amber-600" />
            <div>
              <Label className="cursor-pointer">مع إعاشة</Label>
              <p className="text-xs text-gray-500">
                {selectedRoom.meal_price ? `+${selectedRoom.meal_price.toLocaleString()} دج` : 'لا يوجد سعر إعاشة محدد'}
              </p>
            </div>
          </div>
          <Switch
            checked={!!value.has_meals}
            onCheckedChange={setHasMeals}
            disabled={!selectedRoom.meal_price}
            data-testid="css-meals-switch"
          />
        </div>
      )}

      {/* Summary */}
      {selectedRoom && (
        <div className="bg-brand-50 border border-brand-200 rounded p-3 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-600">السعر الإجمالي</p>
            <p className="text-2xl font-bold text-brand-700 font-mono">{total.toLocaleString()} دج</p>
          </div>
          <div className="text-left text-xs text-gray-600">
            <div>الغرفة: {selectedRoom.price?.toLocaleString()} دج</div>
            {value.has_meals && selectedRoom.meal_price > 0 && (
              <div>الإعاشة: +{selectedRoom.meal_price.toLocaleString()} دج</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CascadingServiceSelector;
